import {gql} from '@apollo/client';
import React from 'react';

import {
  AnalysisWithRelevantItemsQuery,
  AnalysisWithRelevantItemsQueryVariables,
} from 'analysis-tool/ui/metadata-modal/__generated/AnalysisWithRelevantItemsQuery';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import PresentationalRelevantItemsSection from 'relevant-items/PresentationalRelevantItemsSection';
import {RELEVANT_ITEMS_FRAGMENT} from 'relevant-items/RelevantItemsSection';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

export const ANALYSIS_WITH_RELEVANT_ITEMS_QUERY = gql`
  query AnalysisWithRelevantItemsQuery($analysisId: StrictString!)
  @skipAudit
  @engComponent(name: ANALYSIS_TOOL) {
    analysis(id: $analysisId) {
      ... on AnalysisNode {
        id
        relevantItems {
          ...RelevantItemsGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${RELEVANT_ITEMS_FRAGMENT}
`;

interface AnalysisRelevantItemsSectionProps {
  analysisId: string;
}

/**
 * Display the relevant items for an analysis.
 *
 * @package
 */
export default function AnalysisRelevantItemsSection(props: AnalysisRelevantItemsSectionProps): JSX.Element {
  return (
    <BenchlingQuery<AnalysisWithRelevantItemsQuery, AnalysisWithRelevantItemsQueryVariables>
      query={ANALYSIS_WITH_RELEVANT_ITEMS_QUERY}
      variables={{analysisId: props.analysisId}}
      renderLoading={() => <CenteredHorizontalThrobber />}
      renderCachedDataWhenRefetching={true}
      fetchPolicy="no-cache"
    >
      {(data) => {
        if (isInaccessible(data.analysis)) {
          // If this is an analysis that the user doesn't have view-access to, we don't want
          // to render a relevant items section at all
          return null;
        }
        return (
          <PresentationalRelevantItemsSection
            relevantItemsGraphQL={data.analysis.relevantItems}
            itemId={data.analysis.id}
            isCompact={true}
            paginationLimit={5}
            relevantConvertedFromItemsGraphQL={null}
          />
        );
      }}
    </BenchlingQuery>
  );
}
